import * as React from 'react';
import { useState } from 'react';
import { Avatar, Box, Button, Divider, Popover, Stack, Typography } from '@mui/material';
import { useAgent } from '../../../../shared';
import { environment } from '../../../../../environments/environment';
import { Calendar, Mail, Phone, PhoneCall, ShoppingBag, Star, Zap } from 'lucide-react';
import { keycloak } from '@coverright/shared/keycloak';
import { medicareAiTheme } from '@coverright/ui/themes';
import { ScheduleACallButton } from '@coverright/features/calendly/ui/schedule-a-call-button';
import { useContactPhone } from '@coverright/utils';

export function AppBarAgent() {
  const { agent } = useAgent();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const open    = Boolean(anchor);
  const fullName  = agent ? `${agent.firstName} ${agent.lastName}` : '';
  const initials  = agent ? `${agent.firstName[0]}${agent.lastName[0]}`.toUpperCase() : '';
  const avatarSrc = agent?.picUrl ? environment.cdnUrl + agent.picUrl : undefined;
  const bookingUrl = agent?.enrollmentMeetingUrl ?? agent?.consultationUrl ?? '#';
  const phoneNumber = useContactPhone();
  // The brand's number must win over the agent's own number; only fall back
  // to the agent's number when there is no brand phone.
  const agentPhone = phoneNumber.overridden
    ? phoneNumber
    : agent?.phone
      ? { display: agent.phone, tel: agent.phone.replace(/\D/g, '') }
      : null;

  // ── No agent: show phone CTA ───────────────────────────────────────────────
  if (!keycloak.authenticated || !agent) {
    return (
      <Button
        size="small"
        href={`tel:${phoneNumber.tel}`}
        sx={{
          gap: 1,
          px: 1.5,
          color: 'text.secondary',
          fontSize: '0.75rem',
          fontWeight: 600,
          height: 'auto',
          minHeight: 'unset',
          py: '6px',
          '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
        }}
      >
        <Phone size={16} aria-hidden />
        <Typography
          component="span"
          sx={{ display: { xs: 'none', md: 'block' }, fontSize: 'inherit', fontWeight: 'inherit', whiteSpace: 'nowrap' }}
        >
          {phoneNumber.display}
        </Typography>
      </Button>
    );
  }

  // ── Agent trigger ─────────────────────────────────────────────────────────
  return (
    <>
      <Box
        onClick={e => setAnchor(e.currentTarget)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
        aria-describedby={open ? 'agent-popover' : undefined}
      >
        {/* Avatar with green online dot */}
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            src={avatarSrc}
            alt={fullName}
            sx={{
              width: 32, height: 32,
              border: '1px solid #e5e7eb',
              bgcolor: '#0A6C84',
              color: 'white',
              fontSize: 13, fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{
            position: 'absolute', bottom: 0, right: 0,
            width: 10, height: 10, borderRadius: '50%',
            bgcolor: '#22c55e', border: '2px solid #ffffff',
          }} aria-hidden />
        </Box>

        {/* Name — hidden on mobile */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, lineHeight: 1.3 }}>
          <Typography sx={{ fontSize: '0.625rem', color: 'text.secondary', fontWeight: 500 }}>
            Your licensed agent
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#111827' }}>
            {fullName}
          </Typography>
        </Box>
      </Box>

      {/* ── Popover ── */}
      <Popover
        id="agent-popover"
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{zIndex: 1400}}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 260,
              border: '1px solid #e5e7eb',
              borderRadius: 1.5,
              boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            },
          },
        }}
      >
        {/* Body */}
        <Box sx={{ p: 2 }}>

          {/* Contact links */}
          <Stack gap={0.75} sx={{ mb: 1 }}>
            {agentPhone && (
              <Box
                component="a"
                href={`tel:${agentPhone.tel}`}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25,
                  fontSize: 12, fontWeight: 600, color: '#374151',
                  textDecoration: 'none', '&:hover': { color: '#009689' }, transition: 'color 150ms',
                }}
              >
                <Box sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: '#f9fafb', border: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Phone size={11} color="#6b7280" />
                </Box>
                {agentPhone.display}
              </Box>
            )}
            {agent?.email && (
              <Box
                component="a"
                href={`mailto:${agent.email}`}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25,
                  fontSize: 12, fontWeight: 500, color: '#4b5563',
                  textDecoration: 'none', '&:hover': { color: '#009689' }, transition: 'color 150ms',
                }}
              >
                <Box sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: '#f9fafb', border: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Mail size={11} color="#6b7280" />
                </Box>
                {agent.email}
              </Box>
            )}
          </Stack>

          {/* Schedule CTA */}
          <ScheduleACallButton
            variant="outlined"
            color="primary"
            size="medium"
            fullWidth
            sx={{
              bgcolor: '#009689', color: '#fff',
              fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap',
              height: 40,
              mb: 1,
              transition: 'background 150ms', '&:hover': { bgcolor: '#008276' },
            }}
            calendlyProps={{
              agent,
              theme: medicareAiTheme,
            }}
            onClick={() => setAnchor(null)}
          >
            Schedule an appointment
          </ScheduleACallButton>

          {/* Why call us */}
          <Box sx={{ bgcolor: '#fcfaf5', border: '1px solid #f5ebd6', borderRadius: 2, p: 1.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.25 }}>
              Why call us?
            </Typography>
            {[
              { icon: <ShoppingBag size={12} color="#9ca3af" />, text: "It's 100% free" },
              { icon: <PhoneCall size={12} color="#9ca3af" />,   text: 'Unbiased expert advice' },
              { icon: <Zap size={12} color="#9ca3af" />,        text: 'Ready to help you enroll' },
            ].map(({ icon, text }) => (
              <Stack key={text} direction="row" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
                <Box sx={{ flexShrink: 0 }}>{icon}</Box>
                <Typography sx={{ fontSize: 11, color: '#4b5563' }}>{text}</Typography>
              </Stack>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
